import {useCallback, useEffect, useState} from 'react';

/**
 * Viewing preferences: palette and motion. Both are written to <html> as data
 * attributes, which is what index.css keys its [data-theme] / [data-motion]
 * blocks off, and mirrored into localStorage so the inline bootstrap in
 * index.html can apply them before first paint.
 *
 * `hc` is the accessibility toggle the reference site exposes in its utility
 * bar; `still` is the explicit reduced-motion switch, separate from the OS-level
 * prefers-reduced-motion media query so a user can silence the ambient
 * animation without changing a system setting mid-demo.
 */
export type Theme = 'light' | 'dark' | 'hc';
export type Motion = 'full' | 'still';

const THEME_KEY = 'studio-theme';
const MOTION_KEY = 'studio-motion';

export const THEME_ORDER: Theme[] = ['light', 'dark', 'hc'];

export const THEME_LABEL: Record<Theme, string> = {
  light: 'نهاري',
  dark: 'ليلي',
  hc: 'تباين عالٍ',
};

/** Single glyph for the utility-bar button, matching the reference site's ◐. */
export const THEME_GLYPH: Record<Theme, string> = {
  light: '☀',
  dark: '☾',
  hc: '◐',
};

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return allowed.includes(raw as T) ? (raw as T) : fallback;
  } catch {
    // Private mode or blocked storage — the default is always usable.
    return fallback;
  }
}

function persist(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Preference is still applied to the DOM for this session; it just will not
    // survive a reload. Not worth surfacing to the user.
  }
}

export function useViewPrefs() {
  const [theme, setThemeState] = useState<Theme>(() => read(THEME_KEY, THEME_ORDER, 'light'));
  const [motion, setMotionState] = useState<Motion>(() =>
    read(MOTION_KEY, ['full', 'still'] as const, 'full'),
  );

  useEffect(() => {
    // 'light' is the stylesheet default, so the attribute is removed rather than
    // set — keeps the DOM honest about which blocks are actually in play.
    if (theme === 'light') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = theme;
    persist(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    if (motion === 'full') delete document.documentElement.dataset.motion;
    else document.documentElement.dataset.motion = motion;
    persist(MOTION_KEY, motion);
  }, [motion]);

  const cycleTheme = useCallback(() => {
    setThemeState((t) => THEME_ORDER[(THEME_ORDER.indexOf(t) + 1) % THEME_ORDER.length]!);
  }, []);

  const toggleMotion = useCallback(() => {
    setMotionState((m) => (m === 'full' ? 'still' : 'full'));
  }, []);

  return {theme, motion, cycleTheme, toggleMotion};
}
